'use client';

import { useCallback, useRef, useState } from 'react';
import { useWalletSession } from '@/hooks/use-wallet-session';
import type {
  WalletSignerRecoveryAction,
  WalletSignerRecoveryLocation
} from '@/lib/analytics';
import { runWalletSignerRecovery } from '@/lib/wallet-signer-recovery';
import { useWalletSignerHealth } from '@/providers/wallet-signer-health-provider';

/**
 * The three ways out of a signer that no longer matches the signed in
 * wallet: check again, reconnect, or end the session. The wallet gate, the
 * Overview entry and the header pill each used to carry their own copy of
 * these handlers and their pending and failure flags; this is the one copy.
 *
 * `location` names the surface for analytics. Nothing here reads or reports
 * a wallet address.
 */
export function useSignerRecovery(location: WalletSignerRecoveryLocation) {
  const { disconnect: endSession } = useWalletSession();
  const {
    status: signerStatus,
    retry: recheckSigner,
    reconnect: reconnectSigner
  } = useWalletSignerHealth();
  const [pending, setPending] = useState<WalletSignerRecoveryAction | null>(
    null
  );
  const [failed, setFailed] = useState<WalletSignerRecoveryAction | null>(null);
  // State lands a render late. Two activations in one frame must not send
  // two requests to the wallet, so the lock is a ref.
  const inFlight = useRef<WalletSignerRecoveryAction | null>(null);

  const run = useCallback(
    async (action: WalletSignerRecoveryAction) => {
      // Ending the session is the escape hatch: it stays available while a
      // recheck or reconnect hangs on a wallet prompt nobody answers. Only a
      // second disconnect is refused.
      if (
        inFlight.current === 'disconnect' ||
        (inFlight.current !== null && action !== 'disconnect')
      ) {
        return;
      }
      inFlight.current = action;
      setFailed(null);
      setPending(action);

      const succeeded = await runWalletSignerRecovery({
        action,
        location,
        operation:
          action === 'reconnect'
            ? reconnectSigner
            : action === 'recheck'
              ? recheckSigner
              : async () => {
                  await endSession();
                  return 'succeeded';
                }
      });

      // A disconnect that started while this action was in flight owns the
      // pending state now; leave it alone.
      if (inFlight.current !== action) return;
      inFlight.current = null;
      setPending(null);
      setFailed(succeeded ? null : action);
    },
    [endSession, location, reconnectSigner, recheckSigner]
  );

  const recheck = useCallback(() => run('recheck'), [run]);
  const reconnect = useCallback(() => run('reconnect'), [run]);
  const disconnect = useCallback(() => run('disconnect'), [run]);
  /** Reconnect a wallet that lost its connection; otherwise check again. */
  const recover = useCallback(
    () => run(signerStatus === 'disconnected' ? 'reconnect' : 'recheck'),
    [run, signerStatus]
  );
  const clearFailure = useCallback(() => setFailed(null), []);

  return {
    signerStatus,
    /** The action in flight, if any. */
    pending,
    /** The last action that did not succeed. Cleared when another starts. */
    failed,
    recover,
    recheck,
    reconnect,
    disconnect,
    clearFailure
  };
}
